import {
  LoadMessagesResponse,
  SendMessagePayload,
  SendMessageResponse,
  ApiOptions,
  AuthConfig,
} from '../types';

/**
 * Cria headers para requisições HTTP
 */
export const createHeaders = (
  sessionId: string,
  authConfig?: AuthConfig
): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'X-Session-ID': sessionId,
  };

  if (authConfig) {
    headers[authConfig.headerName] = authConfig.headerValue;
  }

  return headers;
};

/**
 * Função genérica para fazer requisições HTTP
 */
export const fetchWithConfig = async <T>(
  url: string,
  options: RequestInit
): Promise<T> => {
  try {
    const response = await fetch(url, options);

    if (!response.ok) {
      throw new Error(
        `HTTP Error: ${response.status} ${response.statusText}`
      );
    }

    // Valida content-type (opcional mas recomendado)
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      console.error('[fetchWithConfig] Invalid content-type:', contentType);
    }

    // Lê como text primeiro para ter controle total e permitir debug
    const text = await response.text();

    try {
      const data = JSON.parse(text);
      return data as T;
    } catch (parseError) {
      // Loga raw response para debug
      console.error('[JSON Parse Error] Raw response (first 500 chars):', text.substring(0, 500));
      console.error('[JSON Parse Error] Full error:', parseError);
      throw new Error(
        `JSON parsing failed: ${parseError instanceof Error ? parseError.message : 'unknown error'}`
      );
    }
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Erro desconhecido na requisição');
  }
};

/**
 * Carrega mensagens do backend
 */
export const loadMessages = async (
  url: string,
  sessionId: string,
  authConfig?: AuthConfig
): Promise<LoadMessagesResponse> => {
  const headers = createHeaders(sessionId, authConfig);

  const response = await fetchWithConfig<LoadMessagesResponse>(url, {
    method: 'GET',
    headers,
  });

  console.log('[API loadMessages] Resposta da API:', {
    url,
    sessionId,
    totalMensagens: response.messages?.length || 0,
    mensagens: response.messages?.map(m => `${m.sender}: ${m.message.substring(0, 20)}...`) || []
  });

  return response;
};

/**
 * Envia mensagem para o backend
 */
export const sendMessage = async (
  url: string,
  payload: SendMessagePayload,
  authConfig?: AuthConfig
): Promise<SendMessageResponse> => {
  const headers = createHeaders(payload.sessionId, authConfig);

  return fetchWithConfig<SendMessageResponse>(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
};

/**
 * Classe de gerenciamento de API (opcional, para casos mais complexos)
 */
export class ApiClient {
  constructor(
    private loadMessagesUrl: string,
    private sendMessageUrl: string,
    private authConfig?: AuthConfig
  ) {}

  async loadMessages(sessionId: string): Promise<LoadMessagesResponse> {
    return loadMessages(this.loadMessagesUrl, sessionId, this.authConfig);
  }

  async sendMessage(
    payload: SendMessagePayload
  ): Promise<SendMessageResponse> {
    return sendMessage(this.sendMessageUrl, payload, this.authConfig);
  }

  updateAuthConfig(authConfig: AuthConfig): void {
    this.authConfig = authConfig;
  }
}
